/**
 * Caption markup for the Agent Outputs view.
 *
 * Ported from the old dashboard so the "Model edits on original caption" and
 * "Final caption with added details" columns render exactly as they did there.
 * These build HTML strings because the marking is done by regex against the caption
 * text, not by character offsets - the pipeline reports spans as quoted text, not
 * positions. Every interpolated value goes through `escapeHtml` first.
 */

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const escapeRegExp = (value) => String(value ?? '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** CSS class for a pipeline label. */
export function captionClassFor(label) {
  switch (label) {
    case 'hallucination': return 'final-caption-removed';
    case 'wrong_uncertainty': return 'final-caption-wrong';
    case 'unverifiable': return 'final-caption-unverifiable';
    case 'uncertain': return 'final-caption-uncertain';
    case 'supported': return 'final-caption-supported';
    default: return 'final-caption-replace';
  }
}

/**
 * A pipeline span may be written as several alternatives joined by " / ", and
 * sometimes prefixed with "from span:". Longest first, so the most specific wins.
 */
export function phraseVariants(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return [];
  const out = [];
  for (const part of trimmed.split(/\s+\/\s+/)) {
    let candidate = part.trim().replace(/^from span:\s*/i, '').trim();
    const inner = candidate.match(/\bfrom span:\s*(.+)$/i);
    if (inner) candidate = inner[1].trim();
    if (candidate) out.push(candidate);
  }
  out.push(trimmed.replace(/^from span:\s*/i, '').trim());
  return [...new Set(out.filter(Boolean))].sort((a, b) => b.length - a.length);
}

/** Replace the first variant that occurs in `html`. */
function replaceFirstVariant(html, variants, wrap) {
  for (const variant of variants) {
    const escaped = escapeHtml(variant);
    const re = new RegExp(escapeRegExp(escaped), 'i');
    if (re.test(String(html || ''))) {
      return String(html || '').replace(re, (match) => wrap(match, variant));
    }
  }
  return html;
}

/** "Model edits on original caption" - the adjudicator's spans marked on the original. */
export function markOriginalCaption(record) {
  let html = escapeHtml(record?.original_caption || '');
  const spans = record?.final_adjudication?.final_spans || [];

  for (const span of spans) {
    const variants = phraseVariants(span.span_text || span.claim_or_span);
    if (!variants.length) continue;

    const label = span.final_label || '';
    const action = span.final_action || '';
    const cls = captionClassFor(label);

    html = replaceFirstVariant(html, variants, (match) => {
      if (action === 'replace' && span.replacement) {
        return (
          `<span class="${cls}" title="${escapeHtml(label)}">${match}</span>` +
          `<span class="final-caption-inserted" title="replacement">${escapeHtml(span.replacement)}</span>`
        );
      }
      if (action === 'remove') {
        return `<span class="final-caption-removed" title="${escapeHtml(label)}">${match}</span>`;
      }
      if (action === 'keep_with_uncertainty_note' && span.uncertainty_note) {
        return (
          `<span class="${cls}" title="${escapeHtml(label)}">${match}</span>` +
          `<span class="final-caption-uncertain" title="uncertainty note">${escapeHtml(span.uncertainty_note)}</span>`
        );
      }
      return `<span class="${cls}" title="${escapeHtml(label)}">${match}</span>`;
    });
  }
  return html;
}

/** Every missing detail the run verified as visible and worth adding. */
function verifiedDetails(record) {
  const out = [];
  const consensus = record?.final_adjudication?.consensus_summary || record?.consensus_summary || {};
  for (const detail of consensus.verified_missing_details || []) {
    if (String(detail || '').trim()) out.push(String(detail).trim());
  }
  for (const round of record?.discussion_rounds || []) {
    for (const agent of Object.values(round.agent_outputs || {})) {
      for (const item of agent?.verified_missing_details || []) {
        if (item?.visible && item?.should_add_to_caption && String(item.detail || '').trim()) {
          out.push(String(item.detail).trim());
        }
      }
    }
  }
  return [...new Set(out)];
}

/** A detail is prose, so try progressively looser forms of it against the caption. */
function detailVariants(detail) {
  const normalized = String(detail || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return [];
  const out = [normalized];

  const afterColon = normalized.split(':').slice(1).join(':').trim();
  if (afterColon) out.push(afterColon);

  out.push(...[...normalized.matchAll(/["“”']([^"“”']{4,})["“”']/g)].map((m) => m[1].trim()));

  const stripped = normalized
    .replace(/^(add|include|mentions?|visible|verified|important|omitted)\b[:\s-]*/i, '')
    .replace(/\b(should be added|is visible|are visible|visible in the image|clearly visible)\b/gi, '')
    .trim();
  if (stripped && stripped !== normalized) out.push(stripped);

  return [...new Set(out.filter((v) => v.length >= 4))].sort((a, b) => b.length - a.length);
}

/** "Final caption with added details" - marks details the run added. */
export function markFinalCaption(record) {
  const caption = record?.final_adjudication?.final_edited_caption || '';
  let html = escapeHtml(caption);

  for (const detail of verifiedDetails(record)) {
    for (const variant of detailVariants(detail)) {
      const before = html;
      const re = new RegExp(escapeRegExp(escapeHtml(variant)), 'i');
      html = html.replace(re, (match) => `<span class="final-caption-added">${match}</span>`);
      if (html !== before) break;
    }
  }
  return html;
}
